import { useMemo } from "react";
import { Order } from "../services/orderApi";
import { formatCurrency } from "../utils/format";

interface Stats {
  totalOrders: number;
  totalRevenue: number;
  subtotalRevenue: number;
  shippingRevenue: number;
  netRevenue: number;
  paidOrders: number;
  pendingOrders: number;
  processingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  refundedOrders: number;
  codOrders: number;
}

interface TopProduct {
  name: string;
  quantity: number;
  revenue: number;
}

interface StatCard {
  label: string;
  value: string | number;
  icon: string;
  color: string;
  bg: string;
  filterType?: "paymentStatus" | "orderStatus";
  filterValue?: string;
  clickable?: boolean;
}

interface AdminOverviewProps {
  orders: Order[];
  onRefresh?: () => void;
  onCardClick?: (filterType: "paymentStatus" | "orderStatus", filterValue: string) => void;
}

export default function AdminOverview({ orders, onRefresh, onCardClick }: AdminOverviewProps) {
  const stats = useMemo<Stats>(() => {
    const totalRevenue = orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
    const shippingRevenue = orders.reduce((sum, o) => sum + Number(o.shippingFee || 0), 0);
    const subtotalRevenue = totalRevenue - shippingRevenue;
    
    const paidOrdersList = orders.filter(o => o.paymentStatus === "paid" || o.paymentStatus === "COD");
    // Admin Earned is only for orders that are BOTH paid AND completed
    const earnedOrdersList = paidOrdersList.filter(o => o.orderStatus === "completed");
    const netRevenue = earnedOrdersList.reduce((sum, o) => sum + (Number(o.totalAmount) - Number(o.shippingFee || 0)), 0);

    return {
      totalOrders: orders.length,
      totalRevenue,
      subtotalRevenue,
      shippingRevenue,
      netRevenue,
      paidOrders: paidOrdersList.length,
      pendingOrders: orders.filter(o => o.paymentStatus === "pending").length,
      codOrders: orders.filter(o => o.paymentStatus === "COD").length,
      processingOrders: orders.filter(o => o.orderStatus === "processing").length,
      shippedOrders: orders.filter(o => o.orderStatus === "shipped").length,
      deliveredOrders: orders.filter(o => o.orderStatus === "delivered").length,
      cancelledOrders: orders.filter(o => o.orderStatus === "cancelled").length,
      refundedOrders: orders.filter(o => o.paymentStatus === "refunded").length,
    };
  }, [orders]);

  const topProducts = useMemo<TopProduct[]>(() => {
    const productMap = new Map<string, { quantity: number; revenue: number }>();
    orders.forEach(order => {
      (order.items || []).forEach(item => {
        const existing = productMap.get(item.name) || { quantity: 0, revenue: 0 };
        productMap.set(item.name, {
          quantity: existing.quantity + item.quantity,
          revenue: existing.revenue + item.totalPrice,
        });
      });
    });

    return Array.from(productMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [orders]);

  const recentOrders = useMemo(() => orders.slice(0, 5), [orders]);

   const statCards: StatCard[] = [
     { label: "Total Orders", value: stats.totalOrders, icon: "fa-box", color: "#6366f1", bg: "#eef2ff" },
     { label: "Total Revenue (All)", value: formatCurrency(stats.totalRevenue), icon: "fa-peso-sign", color: "#10b981", bg: "#d1fae5" },
     { label: "Net Revenue (Earned)", value: formatCurrency(stats.netRevenue), icon: "fa-money-bill-wave", color: "#059669", bg: "#d1fae5" },
     { label: "Product Revenue", value: formatCurrency(stats.subtotalRevenue), icon: "fa-shopping-bag", color: "#f97316", bg: "#ffedd5" },
     { label: "Shipping Revenue", value: formatCurrency(stats.shippingRevenue), icon: "fa-truck", color: "#8b5cf6", bg: "#ede9fe" },
     { label: "Paid Orders", value: stats.paidOrders, icon: "fa-check-circle", color: "#059669", bg: "#d1fae5", filterType: "paymentStatus", filterValue: "paid", clickable: true },
     { label: "Pending Payment", value: stats.pendingOrders, icon: "fa-clock", color: "#f59e0b", bg: "#fef3c7", filterType: "paymentStatus", filterValue: "pending", clickable: true },
      { label: "COD Orders", value: stats.codOrders, icon: "fa-money-bill-wave", color: "#2563eb", bg: "#dbeafe", filterType: "paymentStatus", filterValue: "COD", clickable: true },
     { label: "Processing", value: stats.processingOrders, icon: "fa-spinner", color: "#8b5cf6", bg: "#ede9fe", filterType: "orderStatus", filterValue: "processing", clickable: true },
     { label: "Shipped", value: stats.shippedOrders, icon: "fa-truck", color: "#f97316", bg: "#ffedd5", filterType: "orderStatus", filterValue: "shipped", clickable: true },
     { label: "Delivered", value: stats.deliveredOrders, icon: "fa-check-double", color: "#059669", bg: "#d1fae5", filterType: "orderStatus", filterValue: "delivered", clickable: true },
     { label: "Cancelled", value: stats.cancelledOrders, icon: "fa-times-circle", color: "#dc2626", bg: "#fee2e2", filterType: "orderStatus", filterValue: "cancelled", clickable: true },
     { label: "Refunded", value: stats.refundedOrders, icon: "fa-undo", color: "#6b7280", bg: "#f3f4f6", filterType: "paymentStatus", filterValue: "refunded", clickable: true },
   ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "700", margin: 0 }}>Overview</h2>
        {onRefresh && (
          <button onClick={onRefresh} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="fas fa-sync-alt"></i> Refresh Data
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "32px" }}>
        {statCards.map((card, index) => (
          <div 
            key={index} 
            onClick={() => {
              if (card.clickable && card.filterType && card.filterValue && onCardClick) {
                onCardClick(card.filterType, card.filterValue);
              }
            }}
            className={card.clickable ? "stat-card-hover" : ""}
            style={{ 
              background: "white", 
              padding: "20px", 
              borderRadius: "16px", 
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              cursor: card.clickable ? "pointer" : "default",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>{card.label}</span>
              <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: card.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "14px" }}></i>
              </div>
            </div>
            <p style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a1a1a", margin: 0 }}>{card.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: "24px" }}>
        <div style={{ background: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1a1a1a", marginBottom: "20px" }}>
            <i className="fas fa-star" style={{ color: "#f59e0b", marginRight: "10px" }}></i>
            Top Products
          </h3>
          
          {topProducts.length === 0 ? (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No products yet</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {topProducts.map((product, index) => (
                <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f9fafb", borderRadius: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ width: "24px", height: "24px", borderRadius: "6px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: "700" }}>
                      {index + 1}
                    </span>
                    <span style={{ fontWeight: "500", color: "#1a1a1a" }}>{product.name}</span>
                  </div>
                    <div style={{ textAlign: "right" }}>
                     <p style={{ fontWeight: "600", color: "#f97316", margin: 0 }}>{formatCurrency(product.revenue)}</p>
                     <p style={{ fontSize: "0.75rem", color: "#6b7280", margin: 0 }}>{product.quantity} sold</p>
                   </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1a1a1a", marginBottom: "20px" }}>
            <i className="fas fa-shopping-cart" style={{ color: "#6366f1", marginRight: "10px" }}></i>
            Recent Orders
          </h3>
          
          {recentOrders.length === 0 ? (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No orders yet</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {recentOrders.map((order) => (
                <div key={order.orderId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f9fafb", borderRadius: "10px" }}>
                  <div>
                    <p style={{ fontWeight: "600", color: "#1a1a1a", margin: 0, fontSize: "0.9rem" }}>{order.orderId}</p>
                    <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: 0 }}>{order.customerName}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontWeight: "600", color: "#f97316", margin: 0 }}>{formatCurrency(order.totalAmount)}</p>
                    <span style={{ fontSize: "0.7rem", fontWeight: "600", padding: "2px 8px", borderRadius: "20px", background: order.orderStatus === "delivered" ? "#d1fae5" : order.orderStatus === "shipped" ? "#ede9fe" : order.orderStatus === "cancelled" ? "#fee2e2" : "#fef3c7", color: order.orderStatus === "delivered" ? "#059669" : order.orderStatus === "shipped" ? "#7c3aed" : order.orderStatus === "cancelled" ? "#dc2626" : "#d97706" }}>
                      {order.orderStatus}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
